import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { formatCents } from "../../domain/money";

/**
 * CHART PRIMITIVES (spec §88.4). Plain SVG and CSS — no chart library, no
 * canvas — so they print, scale, and cost nothing to load. Every chart takes
 * values an engine already computed and draws them; none computes a
 * financial figure of its own. Each renders its exact values as text (in
 * the chart or beside it) and carries an accessible summary, so no number
 * lives only in a shape.
 */

export const CHART_COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)", "var(--chart-6)"];

const compact = (cents: number): string => {
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  const sign = dollars < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};
export { compact as compactMoney };

/**
 * SVG charts draw in real CSS pixels: the viewBox follows the element's
 * measured width, so an 11px label is 11px on a phone and on a 27" screen
 * (a fixed viewBox scaled the text with the page).
 */
export function useChartWidth(fallback = 720) {
  const ref = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/* ---------------- Horizontal bar rows ---------------- */

export interface BarRowDatum {
  key: string;
  label: ReactNode;
  cents: number;
  color?: string;
  /** Rendered instead of the formatted amount. */
  valueLabel?: ReactNode;
  title?: string;
}

/** Ranked horizontal bars with the exact amount beside each: composition at a glance. */
export function BarRows({ rows, label, max }: { rows: BarRowDatum[]; label: string; max?: number }) {
  const top = max ?? Math.max(1, ...rows.map((r) => Math.abs(r.cents)));
  return (
    <div className="bar-rows" role="list" aria-label={label}>
      {rows.map((r, i) => (
        <div className="bar-row" role="listitem" key={r.key} title={r.title}>
          <span className="bar-row-label">{r.label}</span>
          <span className="bar-row-track" aria-hidden="true">
            <span style={{ width: `${(Math.abs(r.cents) / top) * 100}%`, background: r.color ?? CHART_COLORS[i % CHART_COLORS.length] }} />
          </span>
          <span className="bar-row-value">{r.valueLabel ?? formatCents(r.cents)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Stacked composition bar ---------------- */

export interface StackSegment {
  key: string;
  label: string;
  cents: number;
  color?: string;
}

export function StackBar({ segments, label }: { segments: StackSegment[]; label: string }) {
  const positive = segments.filter((s) => s.cents > 0);
  const total = positive.reduce((s, x) => s + x.cents, 0);
  return (
    <div>
      <div
        className="stack-bar"
        role="img"
        aria-label={`${label}: ${positive.map((s) => `${s.label} ${formatCents(s.cents)}`).join(", ")}`}
      >
        {positive.map((s, i) => (
          <span
            key={s.key}
            title={`${s.label}: ${formatCents(s.cents)}`}
            style={{ width: `${total ? (s.cents / total) * 100 : 0}%`, background: s.color ?? CHART_COLORS[i % CHART_COLORS.length] }}
          />
        ))}
      </div>
      <div className="chart-legend">
        {positive.map((s, i) => (
          <span key={s.key}>
            <i style={{ background: s.color ?? CHART_COLORS[i % CHART_COLORS.length] }} />
            {s.label} <strong>{formatCents(s.cents)}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Time series (step line with zero line) ---------------- */

export interface SeriesPoint {
  date: string;
  cents: number;
}

/**
 * A running balance over time drawn as steps (money moves on dates, not
 * smoothly between them), with the zero line emphasised and the lowest point
 * labelled.
 */
export function StepLine({ points, label, height = 180 }: { points: SeriesPoint[]; label: string; height?: number }) {
  const { ref, width: measured } = useChartWidth();
  const geometry = useMemo(() => {
    if (points.length === 0) return null;
    const width = measured;
    const pad = { top: 16, right: 16, bottom: 26, left: 56 };
    const days = points.map((p) => Date.parse(p.date));
    const minT = Math.min(...days);
    const maxT = Math.max(...days, minT + 86_400_000);
    const values = points.map((p) => p.cents);
    const minV = Math.min(0, ...values);
    const maxV = Math.max(0, ...values);
    const span = maxV - minV || 1;
    const x = (t: number) => pad.left + ((t - minT) / (maxT - minT)) * (width - pad.left - pad.right);
    const y = (v: number) => pad.top + (1 - (v - minV) / span) * (height - pad.top - pad.bottom);
    let d = `M ${x(days[0])} ${y(values[0])}`;
    for (let i = 1; i < points.length; i += 1) d += ` H ${x(days[i])} V ${y(values[i])}`;
    const lowest = points.reduce((a, b) => (b.cents < a.cents ? b : a));
    const ticks = [maxV, 0, minV].filter((v, i, all) => all.indexOf(v) === i);
    return { width, pad, d, x, y, lowest, ticks, minT, maxT, days };
  }, [points, height, measured]);
  if (!geometry) return null;
  const { width, pad, d, x, y, lowest, ticks, minT, maxT } = geometry;
  const fmtDate = (t: number) => new Date(t).toISOString().slice(0, 10);
  return (
    <svg
      ref={ref}
      className="chart"
      viewBox={`0 0 ${width} ${height}`}
      style={{ height }}
      role="img"
      aria-label={`${label}. Lowest ${formatCents(lowest.cents)} on ${lowest.date}.`}
    >
      {ticks.map((v) => (
        <g key={v}>
          <line className={v === 0 ? "chart-axis" : "chart-grid"} x1={pad.left} x2={width - pad.right} y1={y(v)} y2={y(v)} />
          <text x={pad.left - 8} y={y(v) + 4} textAnchor="end">
            {compact(v)}
          </text>
        </g>
      ))}
      <path d={d} fill="none" stroke="var(--chart-1)" strokeWidth="2.25" strokeLinejoin="round" />
      <circle cx={x(Date.parse(lowest.date))} cy={y(lowest.cents)} r="4" fill={lowest.cents < 0 ? "var(--bad-600)" : "var(--chart-1)"} />
      <text x={x(Date.parse(lowest.date)) + 8} y={y(lowest.cents) + 14}>
        Lowest {compact(lowest.cents)}
      </text>
      <text x={pad.left} y={height - 6}>
        {fmtDate(minT)}
      </text>
      <text x={width - pad.right} y={height - 6} textAnchor="end">
        {fmtDate(maxT)}
      </text>
    </svg>
  );
}

/* ---------------- Distribution with a subject marker ---------------- */

export interface RangeDatum {
  key: string;
  value: number;
  label: string;
  tone?: "primary" | "muted" | "excluded";
}

/**
 * Points on one axis (e.g. $/sq ft of comparable sales) with the subject
 * drawn as a distinct marker. Values are plain numbers; `format` renders them.
 */
export function RangeDots({
  data,
  subject,
  format,
  label,
}: {
  data: RangeDatum[];
  subject?: { value: number; label: string } | null;
  format: (value: number) => string;
  label: string;
}) {
  const { ref, width } = useChartWidth();
  const height = 92;
  const values = [...data.map((d) => d.value), ...(subject ? [subject.value] : [])];
  if (values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || Math.max(1, Math.abs(max) * 0.1);
  const lo = min - span * 0.08;
  const hi = max + span * 0.08;
  const x = (v: number) => 24 + ((v - lo) / (hi - lo)) * (width - 48);
  return (
    <svg
      ref={ref}
      className="chart"
      viewBox={`0 0 ${width} ${height}`}
      style={{ height }}
      role="img"
      aria-label={`${label}. ${data.length} values from ${format(min)} to ${format(max)}${subject ? `; ${subject.label} ${format(subject.value)}` : ""}.`}
    >
      <line className="chart-axis" x1={24} x2={width - 24} y1={46} y2={46} />
      {data.map((d, i) => (
        <circle
          key={d.key}
          cx={x(d.value)}
          cy={46 + ((i % 3) - 1) * 9}
          r={6}
          fill={d.tone === "excluded" ? "none" : d.tone === "muted" ? "var(--chart-6)" : "var(--chart-2)"}
          stroke={d.tone === "excluded" ? "var(--gray-400)" : "#fff"}
          strokeWidth={1.5}
          opacity={d.tone === "excluded" ? 0.8 : 0.9}
        >
          <title>{`${d.label}: ${format(d.value)}`}</title>
        </circle>
      ))}
      {subject && (
        <g>
          <line x1={x(subject.value)} x2={x(subject.value)} y1={14} y2={70} stroke="var(--brand-700)" strokeWidth={2} strokeDasharray="3 3" />
          <text x={x(subject.value)} y={10} textAnchor={x(subject.value) < 90 ? "start" : x(subject.value) > width - 90 ? "end" : "middle"} style={{ fill: "var(--brand-800)", fontWeight: 600 }}>
            {subject.label} {format(subject.value)}
          </text>
        </g>
      )}
      <text x={24} y={88}>
        {format(min)}
      </text>
      <text x={width - 24} y={88} textAnchor="end">
        {format(max)}
      </text>
    </svg>
  );
}

/* ---------------- Tornado (sensitivity) ---------------- */

export interface TornadoRow {
  key: string;
  label: string;
  lowLabel: string;
  highLabel: string;
  lowCents: number | null;
  highCents: number | null;
}

/** How far one result moves when each assumption moves, around the base case. */
export function Tornado({ rows, baseCents, label }: { rows: TornadoRow[]; baseCents: number; label: string }) {
  const deltas = rows.flatMap((r) => [r.lowCents, r.highCents]).filter((v): v is number => v !== null).map((v) => Math.abs(v - baseCents));
  const reach = Math.max(1, ...deltas);
  return (
    <div className="tornado" role="list" aria-label={label}>
      {rows.map((r) => {
        const low = r.lowCents === null ? 0 : r.lowCents - baseCents;
        const high = r.highCents === null ? 0 : r.highCents - baseCents;
        const seg = (delta: number) => ({
          left: `${50 + Math.min(0, delta / reach) * 50}%`,
          width: `${(Math.abs(delta) / reach) * 50}%`,
        });
        return (
          <div className="tornado-row" role="listitem" key={r.key}>
            <span className="tornado-label">{r.label}</span>
            <span className="tornado-track" aria-hidden="true">
              <span className="tornado-mid" />
              <span className={`tornado-seg ${low < 0 ? "neg" : "pos"}`} style={seg(low)} />
              <span className={`tornado-seg ${high < 0 ? "neg" : "pos"}`} style={seg(high)} />
            </span>
            <span className="tornado-values">
              <span title={r.lowLabel}>{r.lowCents === null ? "—" : compact(r.lowCents)}</span>
              <span className="muted">↔</span>
              <span title={r.highLabel}>{r.highCents === null ? "—" : compact(r.highCents)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Side-by-side comparison ---------------- */

export interface CompareSeries {
  key: string;
  label: string;
  color: string;
  /** Drawn hatched: e.g. money recorded so far on unfinished work. */
  partial?: boolean;
}

export interface CompareRow {
  key: string;
  label: ReactNode;
  /** One value per series, in series order; null = no such figure (drawn as a dash, never as zero). */
  values: (number | null)[];
  note?: ReactNode;
}

/**
 * Several figures per row on ONE shared scale — Proposed / Quote / Actual by
 * category (spec §88A-10). Every bar prints its exact amount; a missing
 * figure prints "—" and draws nothing, so "no quote" can never read as $0.
 */
export function CompareBars({ series, rows, label }: { series: CompareSeries[]; rows: CompareRow[]; label: string }) {
  const top = Math.max(1, ...rows.flatMap((r) => r.values.map((v) => Math.abs(v ?? 0))));
  return (
    <div className="compare-bars">
      <div className="chart-legend compare-legend">
        {series.map((s) => (
          <span key={s.key}>
            <i className={s.partial ? "is-partial" : ""} style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
      <div role="list" aria-label={label}>
        {rows.map((row) => (
          <div className="compare-row" role="listitem" key={row.key}>
            <span className="compare-label">
              {row.label}
              {row.note && <span className="compare-note">{row.note}</span>}
            </span>
            <span className="compare-bars-stack">
              {series.map((s, i) => {
                const value = row.values[i];
                return (
                  <span className="compare-bar-line" key={s.key}>
                    <span className="compare-track" aria-hidden="true">
                      {value !== null && (
                        <span className={s.partial ? "is-partial" : ""} style={{ width: `${(Math.abs(value) / top) * 100}%`, background: s.color }} />
                      )}
                    </span>
                    <span className="compare-value">
                      <span className="visually-hidden">{s.label}: </span>
                      {value === null ? "—" : formatCents(value)}
                    </span>
                  </span>
                );
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
